import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    FilePlus,
    FolderPlus,
    LayoutTemplate,
    Pencil,
    Trash2,
    Archive,
    ArrowRight,
    RotateCcw,
} from 'lucide-react';

export default function ContextMenu({ x, y, anchor, item, onClose, onAction, isAdmin }) {
    const ref = useRef(null);
    const [pos, setPos] = useState(null);

    // `useLayoutEffect`, et pas `useEffect` : la position définitive se MESURE
    // (largeur et hauteur réelles du menu), donc elle ne peut être connue
    // qu'une fois le menu dans le DOM. Avec un effet ordinaire, le navigateur
    // peignait d'abord la position provisoire — le menu apparaissait à droite
    // puis SAUTAIT à gauche. Ici, la mesure et le replacement ont lieu avant la
    // peinture ; `visibility` couvre le cas où le premier rendu s'afficherait
    // quand même (aucune position n'est encore calculée).
    useLayoutEffect(() => {
        const menuW = ref.current?.offsetWidth || 190;
        const menuH = ref.current?.offsetHeight || 260;
        let newTop, newLeft;

        if (anchor === 'below-right') {
            // Le menu s'ouvre À DROITE de son déclencheur : il prolonge la ligne
            // au lieu de recouvrir l'arborescence, qui est ce qu'on vise juste
            // après. Il ne repasse à gauche que s'il déborderait de la fenêtre.
            newLeft = x + 4;
            if (newLeft + menuW > window.innerWidth - 8) newLeft = Math.max(8, x - menuW - 4);
            newTop = y + menuH > window.innerHeight ? Math.max(8, y - menuH - 30) : y;
        } else {
            newTop = y + menuH > window.innerHeight ? Math.max(8, y - menuH) : y;
            newLeft = Math.max(8, Math.min(x, window.innerWidth - menuW - 8));
        }

        setPos({ top: newTop, left: newLeft });
    }, [x, y, anchor]);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [onClose]);

    const isDir = item.type === 'directory';
    const isArchived = !!item.archived;

    return createPortal(
        <div ref={ref} className="fixed z-[200] bg-white border border-stone-200 rounded-xl shadow-xl py-1 min-w-[180px]" style={{ top: pos?.top ?? y, left: pos?.left ?? x, visibility: pos ? undefined : 'hidden' }}>
            {!isArchived && isDir && (
                <>
                    {/* Les MÊMES trois gestes que la barre d'un espace
                        (space.yaml) et que l'écran d'un site : une page vierge,
                        une page reprise d'un modèle, un répertoire. Créer
                        d'après un modèle ne se faisait qu'en haut d'écran, sur
                        le répertoire courant — jamais sur le répertoire visé. */}
                    <button type="button" onClick={() => onAction('new-page')} className="ctx-btn"><FilePlus size={14} className="text-stone-400" /> Page vierge</button>
                    <button type="button" onClick={() => onAction('new-page-template')} className="ctx-btn"><LayoutTemplate size={14} className="text-stone-400" /> À partir d'un modèle</button>
                    <button type="button" onClick={() => onAction('new-dir')} className="ctx-btn"><FolderPlus size={14} className="text-stone-400" /> Nouveau répertoire</button>
                    <div className="border-t border-stone-100 my-1" />
                </>
            )}
            {!isArchived && (
                <>
                    <button type="button" onClick={() => onAction('rename')} className="ctx-btn"><Pencil size={14} className="text-stone-400" /> Renommer</button>
                    {isDir && (
                        <button type="button" onClick={() => onAction('move')} className="ctx-btn"><ArrowRight size={14} className="text-stone-400" /> Déplacer</button>
                    )}
                    <button type="button" onClick={() => onAction('archive')} className="ctx-btn text-blue-600 hover:!bg-blue-50"><Archive size={14} className="text-blue-400" /> Archiver</button>
                </>
            )}
            {isArchived && isAdmin && (
                <button type="button" onClick={() => onAction('unarchive')} className="ctx-btn text-green-600 hover:!bg-green-50"><RotateCcw size={14} className="text-green-400" /> Désarchiver</button>
            )}
            {!isArchived && (
                <button type="button" onClick={() => onAction('delete')} className="ctx-btn text-red-600 hover:!bg-red-50"><Trash2 size={14} className="text-red-400" /> Supprimer</button>
            )}
        </div>,
        document.body,
    );
}
